import React, { useEffect, useState, useMemo, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Pencil, Copy, Trash2, ShieldCheck } from 'lucide-react';
import Button from '../UI/Button';
import Input from '../UI/Input';
import { AgTableCard } from '../UI';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import DeleteTemplateModal from './DeleteTemplateModal';
import TemplateLevelModal from './TemplateLevelModal';
import { GlobalTemplateContext } from '../../contexts/GlobalTemplateContext';
import { useUserContext } from '../../contexts/UserContext';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { saveTemplates } from '../../utils/APIs';

const ManageGlobalTemplates = () => {
    const navigate = useNavigate();
    const { rawData, setRawData, getTemplatesRawData, setData, loading, setLoading, selectedTemplateId, setSelectedTemplateId } = useContext(GlobalTemplateContext);
    const { content } = useContentfulContext();
    const { userData } = useUserContext();

    const [selectedAction, setSelectedAction] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [rowData, setRowData] = useState([]);
    const [gridApi, setGridApi] = useState(null);

    useEffect(() => {
        getTemplatesRawData();
    }, []);

    useEffect(() => {
        if (selectedTemplateId && selectedTemplateId.length > 0) {
            const found = rawData?.find((item) => String(item._id) === String(selectedTemplateId));
            if (found) {
                setSelectedTemplate(found);
            }
        }
    }, [selectedTemplateId, rawData]);

    const extractUserName = (val) => {
        if (!val) return "-";
        if (typeof val === 'string') return val;
        if (typeof val === 'object') return val.name || val.email || val.username || "-";
        return String(val);
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return "-";
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return "-";
        const optionsDate = {
            month: 'short',
            day: '2-digit',
            year: 'numeric'
        };
        const formattedDate = date.toLocaleDateString('en-US', optionsDate);
        const hours12 = date.getHours() % 12 || 12;
        const ampm = date.getHours() >= 12 ? 'PM' : 'AM';
        const formattedTime = hours12 + ':' + ('0' + date.getMinutes()).slice(-2) + ' ' + ampm;
        return `${formattedDate}, ${formattedTime}`;
    };

    useEffect(() => {
        const filteredData = (rawData || []).filter((item) => item.scope === 'global');
        const formattedData = filteredData.map((item) => ({
            template_name: item.name,
            id: item._id,
            last_edited: formatDate(item.updatedAt),
            rawUpdatedAt: item.updatedAt,
            managed_by: item.managed_by,
            levels: item.levels || [],
            rawItem: item
        }));

        formattedData.sort((a, b) => new Date(b.rawUpdatedAt) - new Date(a.rawUpdatedAt));
        setRowData(formattedData);
    }, [rawData]);

    const colDefs = useMemo(() => [
        {
            headerName: content.MANAGE_GLOBAL_TEMPLATE_CONTENT.table_headers.template_name,
            field: "template_name",
            filter: true,
            cellRenderer: ({ data }) => {
                if (!data) return null;
                return (
                    <div className="flex items-center gap-2 min-w-0">
                        <button
                            type="button"
                            className="min-w-0 truncate text-left font-medium text-xs md:text-sm text-primary hover:underline cursor-pointer bg-transparent border-0 p-0"
                            onClick={() => {
                                navigate(`/admin/global-template/edit-global-template/${data.id}`);
                            }}
                        >
                            {data.template_name}
                        </button>
                    </div>
                );
            },
            suppressMovable: true
        },
        {
            headerName: content.MANAGE_GLOBAL_TEMPLATE_CONTENT.table_headers.accessibility,
            field: "accessibility",
            cellRenderer: ({ data }) => {
                if (!data) return null;
                const hasLevels = data?.levels && data.levels.length > 0;
                return (
                    <div className="flex items-center justify-between gap-2 w-full pr-2">
                        <div className="flex items-center gap-1.5 flex-wrap min-w-0">
                            {hasLevels ? (
                                data.levels.map((lvl) => (
                                    <span key={lvl} className="stride-badge-pill bg-indigo-50 text-indigo-700 border border-indigo-200/60">
                                        {lvl}
                                    </span>
                                ))
                            ) : (
                                <span className="text-xs text-stride-muted italic">All Organizations</span>
                            )}
                        </div>
                        <button
                            type="button"
                            className="p-1 text-stride-muted hover:text-primary hover:bg-surface-alt rounded-md transition-colors cursor-pointer border-0 bg-transparent flex-shrink-0"
                            title="Edit Levels"
                            onClick={() => {
                                setSelectedAction('accessibility');
                                setSelectedTemplateId(data.id);
                                setSelectedTemplate(data.rawItem || data);
                            }}
                        >
                            <ShieldCheck size={15} />
                        </button>
                    </div>
                );
            },
            suppressMovable: true,
            sortable: false,
            resizable: true
        },
        {
            headerName: content.MANAGE_GLOBAL_TEMPLATE_CONTENT.table_headers.last_edited,
            field: "last_edited",
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            cellRenderer: ({ data }) => {
                return (
                    <span className="text-xs text-stride-muted tabular-nums">
                        {data?.last_edited || "-"}
                    </span>
                );
            },
            suppressMovable: true,
            sortable: true
        },
        {
            headerName: content.MANAGE_GLOBAL_TEMPLATE_CONTENT.table_headers.managed_by,
            field: "managed_by",
            cellRenderer: ({ data }) => {
                return (
                    <span className="text-xs text-stride-body font-medium truncate block">
                        {extractUserName(data?.managed_by)}
                    </span>
                );
            },
            sortable: false,
            resizable: false,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 140,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                if (!data) return null;
                const fullTemplate = (rawData || []).find((x) => x._id === data.id) || data.rawItem || data;
                return (
                    <div className="stride-table-actions">
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-edit"
                            title={content.MANAGE_GLOBAL_TEMPLATE_CONTENT.more_info.edit}
                            onClick={() => {
                                navigate(`/admin/global-template/edit-global-template/${data.id}`);
                            }}
                        >
                            <Pencil size={14} />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-copy"
                            title={content.MANAGE_GLOBAL_TEMPLATE_CONTENT.more_info.accessibility}
                            onClick={() => {
                                setSelectedAction('accessibility');
                                setSelectedTemplateId(data.id);
                                setSelectedTemplate(fullTemplate);
                            }}
                        >
                            <ShieldCheck size={14} />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-copy"
                            title={content.MANAGE_GLOBAL_TEMPLATE_CONTENT.more_info.duplicate}
                            onClick={() => {
                                setSelectedTemplate(fullTemplate);
                                duplicateTemplateHandler(fullTemplate);
                            }}
                        >
                            <Copy size={14} />
                        </button>
                        <button
                            type="button"
                            className="stride-table-btn stride-table-btn-delete"
                            title={content.MANAGE_GLOBAL_TEMPLATE_CONTENT.more_info.delete}
                            onClick={() => {
                                setSelectedAction('delete');
                                setSelectedTemplateId(data.id);
                                setSelectedTemplate(fullTemplate);
                            }}
                        >
                            <Trash2 size={14} />
                        </button>
                    </div>
                );
            },
        },
    ], [content, navigate, rawData, setSelectedTemplateId]);

    const onFilterTextBoxChanged = (event) => {
        const filterValue = (event?.target?.value || '').toLowerCase();
        if (gridApi) {
            gridApi.setFilterModel(filterValue ? {
                template_name: { type: 'contains', filter: filterValue }
            } : null);
        }
    };

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("manage-global-templates-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'last_edited', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 400,
            columnLimits: [
                { colId: "template_name", minWidth: 280 },
                { colId: "accessibility", minWidth: 200 },
                { colId: "last_edited", minWidth: 160 },
                { colId: "managed_by", minWidth: 140 },
                { colId: "actions", minWidth: 140 }
            ],
        };
    }, []);

    const isNameUnique = (name) => {
        return !rawData.some((item) => item.name === name);
    };

    const duplicateName = (templateName) => {
        let newName = `${templateName} Copy`;
        let counter = 1;
        while (!isNameUnique(newName)) {
            newName = `${templateName} Copy ${counter++}`;
        }
        return newName;
    };

    const duplicateTemplateHandler = async (targetTemplate = selectedTemplate) => {
        const tpl = targetTemplate || selectedTemplate;
        if (!tpl) return;
        try {
            setLoading(true);
            const payload = {
                name: duplicateName(tpl.name || tpl.template_name),
                html: tpl.html,
                css: tpl.css,
                type: tpl.type,
                config_json: tpl.config_json,
                json_schema: tpl.json_schema,
                levels: tpl.levels
            };
            const result = await saveTemplates(
                userData?.token,
                userData?.selectedOrganization?.org_token,
                payload,
                "global"
            );
            const newRawData = [...rawData, result.data];
            setRawData(newRawData);
            setSelectedTemplateId(result.data._id);
            navigate(`/admin/global-template/edit-global-template/${result.data._id}`);
        } catch (error) {
            console.error('Error duplicating global template:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <main id="main" className="stride-page-shell bg-gradient-to-b from-surface via-white to-white min-h-screen space-y-6">
            {/* Header & Actions */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <p className="stride-eyebrow">Admin & Governance</p>
                    <div className="flex items-baseline gap-3">
                        <h1 className="text-2xl md:text-3xl stride-heading m-0">
                            {content.MANAGE_GLOBAL_TEMPLATE_CONTENT.title}
                        </h1>
                        <span className="text-xs md:text-sm text-stride-muted font-normal">
                            {rowData.length} {rowData.length === 1 ? 'template' : 'templates'}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 flex-wrap sm:flex-nowrap">
                    <Button
                        variant="primary"
                        size="sm"
                        onClick={() => {
                            navigate('/admin/global-template/create-global-template');
                            setData([]);
                            setSelectedTemplateId('');
                        }}
                        icon={<Plus size={15} />}
                    >
                        {content.MANAGE_GLOBAL_TEMPLATE_CONTENT.cta_text.create_template}
                    </Button>
                </div>
            </div>

            {/* Filter and Table Container */}
            <AgTableCard
                header={
                    <div className="w-full sm:w-80">
                        <Input
                            prefixIcon={<Search size={15} />}
                            placeholder={content.MANAGE_GLOBAL_TEMPLATE_CONTENT.search_placeholder}
                            onChange={onFilterTextBoxChanged}
                            clearable
                        />
                    </div>
                }
                tableId="manage-global-templates-table"
                theme="ag-theme-quartz"
                loading={loading}
                rowData={rowData}
                columnDefs={colDefs}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={onGridReady}
                pagination={true}
                paginationPageSize={20}
                paginationPageSizeSelector={[10, 20, 50, 100]}
            />

            {selectedAction === 'delete' && (
                <DeleteTemplateModal
                    setSelectedAction={setSelectedAction}
                    selectedTemplateId={selectedTemplateId}
                />
            )}
            {selectedAction === 'accessibility' && (
                <TemplateLevelModal
                    setSelectedAction={setSelectedAction}
                    selectedTemplateId={selectedTemplateId}
                    levels={selectedTemplate?.levels}
                />
            )}
        </main>
    );
};

export default ManageGlobalTemplates;
